import { motion } from 'motion/react';
import { Container } from '@/components/ui/Container';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { staggerContainer, fadeInUp } from '@/lib/animations';
import { useTranslation } from 'react-i18next';
import {
  UserGroupIcon,
  FolderOpenIcon,
  BookOpenIcon,
  ClipboardDocumentListIcon,
  SparklesIcon,
  BellAlertIcon,
  ClockIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';

export function Features() {
  const { t } = useTranslation();
  const { ref, isInView } = useScrollAnimation();

  const features = [
    {
      icon: UserGroupIcon,
      title: t('features.clientPortal.title'),
      description: t('features.clientPortal.description'),
      badge: t('features.clientPortal.badge'),
      badgeVariant: 'primary' as const,
    },
    {
      icon: FolderOpenIcon,
      title: t('features.projectManagement.title'),
      description: t('features.projectManagement.description'),
      badge: t('features.projectManagement.badge'),
      badgeVariant: 'primary' as const,
    },
    {
      icon: BookOpenIcon,
      title: t('features.methodologyLibrary.title'),
      description: t('features.methodologyLibrary.description'),
      badge: t('features.methodologyLibrary.badge'),
      badgeVariant: 'success' as const,
    },
    {
      icon: ClipboardDocumentListIcon,
      title: t('features.diagnostics.title'),
      description: t('features.diagnostics.description'),
      badge: t('features.diagnostics.badge'),
      badgeVariant: 'new' as const,
    },
    {
      icon: SparklesIcon,
      title: t('features.ai.title'),
      description: t('features.ai.description'),
      badge: t('features.ai.badge'),
      badgeVariant: 'warning' as const,
    },
    {
      icon: BellAlertIcon,
      title: t('features.notifications.title'),
      description: t('features.notifications.description'),
      badge: t('features.notifications.badge'),
      badgeVariant: 'primary' as const,
    },
    {
      icon: ClockIcon,
      title: t('features.activityHistory.title'),
      description: t('features.activityHistory.description'),
      badge: t('features.activityHistory.badge'),
      badgeVariant: 'primary' as const,
    },
    {
      icon: LinkIcon,
      title: t('features.publicLinks.title'),
      description: t('features.publicLinks.description'),
      badge: t('features.publicLinks.badge'),
      badgeVariant: 'primary' as const,
    },
  ];

  return (
    <section id="features" className="py-16 md:py-24 bg-gray-50 dark:bg-slate-900/50">
      <Container>
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {/* Section header */}
          <motion.div variants={fadeInUp} className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
              {t('features.title')}{' '}
              <span className="text-gradient">{t('features.titleHighlight')}</span>
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-slate-400 max-w-2xl mx-auto">
              {t('features.subtitle')}
            </p>
          </motion.div>

          {/* Features grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="h-full" hover>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/20 rounded-xl flex items-center justify-center">
                      <feature.icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                    <Badge variant={feature.badgeVariant}>{feature.badge}</Badge>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-slate-400">
                    {feature.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Additional features note */}
          <motion.div variants={fadeInUp} className="mt-12 text-center">
            <p className="text-gray-500 dark:text-slate-500">
              {t('features.additionalFeatures')}
            </p>
          </motion.div>
        </motion.div>
      </Container>
    </section>
  );
}
